/**
 * Spawn Planner Page
 * AIDLC Administrative Backend
 *
 * The agent spawn orchestration interface. Shows a card grid of spawn
 * plans with status filtering, and a detail view with phase timeline,
 * agent assignments, budget summary, and action buttons (approve /
 * execute). Consumes the backend /api/spawn-plans endpoints.
 *
 * Personas: Ops, Orchestrator
 */

import { useState, useEffect, useCallback } from 'react';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import type {
  SpawnPlan,
  SpawnPlanStatus,
  SpawnPlanPhase,
  SpawnPlanAgent,
  SpawnAgentStatus,
} from '../../services/orchestrationService';
import {
  getSpawnPlans,
  approveSpawnPlan,
  executeSpawnPlan,
} from '../../services/orchestrationService';

import { fmtTokens, fmtDate } from '../../utils/formatters';

/** Plan status badge config */
const planStatusConfig: Record<
  SpawnPlanStatus,
  { label: string; variant: 'neutral' | 'warning' | 'success' | 'info' | 'danger' | 'purple' }
> = {
  draft: { label: 'Draft', variant: 'neutral' },
  pending_approval: { label: 'Pending Approval', variant: 'warning' },
  approved: { label: 'Approved', variant: 'info' },
  executing: { label: 'Executing', variant: 'purple' },
  completed: { label: 'Completed', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'danger' },
};

/** Phase status badge config */
const phaseStatusConfig: Record<
  string,
  { label: string; variant: 'neutral' | 'warning' | 'success' | 'info' | 'danger' | 'purple' }
> = {
  pending: { label: 'Pending', variant: 'neutral' },
  in_progress: { label: 'In Progress', variant: 'purple' },
  completed: { label: 'Completed', variant: 'success' },
  failed: { label: 'Failed', variant: 'danger' },
};

/** Agent status badge config */
const agentStatusConfig: Record<
  SpawnAgentStatus,
  { label: string; variant: 'neutral' | 'warning' | 'success' | 'info' | 'danger' | 'purple' }
> = {
  pending: { label: 'Pending', variant: 'neutral' },
  spawned: { label: 'Spawned', variant: 'purple' },
  completed: { label: 'Completed', variant: 'success' },
  failed: { label: 'Failed', variant: 'danger' },
};

/** Agent avatar color based on agent ID */
function agentAvatarColor(agentId: string): string {
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-amber-500',
    'bg-red-500',
    'bg-teal-500',
    'bg-indigo-500',
    'bg-pink-500',
    'bg-orange-500',
    'bg-cyan-500',
  ];
  let hash = 0;
  for (let i = 0; i < agentId.length; i++) {
    hash = agentId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

type FilterOption = 'all' | SpawnPlanStatus;

// ── Main Component ───────────────────────────────────────────────────

export function SpawnPlannerPage() {
  const [plans, setPlans] = useState<SpawnPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterOption>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const loadPlans = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getSpawnPlans();
      setPlans(data);
    } catch (err) {
      console.error('[SpawnPlanner] Failed to load:', err);
      setError('Failed to load spawn plans. Please check the backend connection.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const filteredPlans = filter === 'all'
    ? plans
    : plans.filter((p) => p.status === filter);

  const counts: Record<FilterOption, number> = {
    all: plans.length,
    draft: plans.filter((p) => p.status === 'draft').length,
    pending_approval: plans.filter((p) => p.status === 'pending_approval').length,
    approved: plans.filter((p) => p.status === 'approved').length,
    executing: plans.filter((p) => p.status === 'executing').length,
    completed: plans.filter((p) => p.status === 'completed').length,
    cancelled: plans.filter((p) => p.status === 'cancelled').length,
  };

  const selectedPlan = selectedId
    ? plans.find((p) => p.id === selectedId) || null
    : null;

  const handleAction = async () => {
    await loadPlans();
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading spawn plans..." />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-red-500 mb-4">
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-gray-700 font-medium mb-2">Unable to Load Spawn Plans</p>
        <p className="text-sm text-gray-500 mb-4 max-w-md text-center">{error}</p>
        <button
          onClick={loadPlans}
          className="px-4 py-2 bg-ldaja-blue text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Detail View
  if (selectedPlan) {
    return (
      <PlanDetailView
        plan={selectedPlan}
        onBack={() => setSelectedId(null)}
        onAction={handleAction}
      />
    );
  }

  // List View
  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-ldaja-black">Spawn Planner</h1>
          <p className="text-sm text-gray-500 mt-1">
            Plan and orchestrate agent spawning sequences. Review phases, approve plans, and execute deployments.
          </p>
        </div>
        <button
          onClick={loadPlans}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-1 mb-6 flex-wrap">
        {([
          { key: 'all' as FilterOption, label: 'All' },
          { key: 'draft' as FilterOption, label: 'Draft' },
          { key: 'pending_approval' as FilterOption, label: 'Pending' },
          { key: 'approved' as FilterOption, label: 'Approved' },
          { key: 'executing' as FilterOption, label: 'Executing' },
          { key: 'completed' as FilterOption, label: 'Completed' },
          { key: 'cancelled' as FilterOption, label: 'Cancelled' },
        ]).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              filter === key
                ? 'bg-ldaja-blue text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {label} ({counts[key]})
          </button>
        ))}
      </div>

      {/* Plan Cards Grid */}
      {filteredPlans.length === 0 ? (
        <EmptyState
          title="No spawn plans found"
          description={
            filter === 'all'
              ? 'No spawn plans have been created yet. Sam will generate plans when assessments indicate readiness.'
              : `No plans with status "${planStatusConfig[filter as SpawnPlanStatus]?.label || filter}".`
          }
          action={
            filter !== 'all' ? (
              <button
                onClick={() => setFilter('all')}
                className="text-sm text-ldaja-blue hover:underline"
              >
                Clear filter
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredPlans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              onClick={() => setSelectedId(plan.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Plan Card ────────────────────────────────────────────────────────

function PlanCard({ plan, onClick }: { plan: SpawnPlan; onClick: () => void }) {
  const status = planStatusConfig[plan.status] || planStatusConfig.draft;
  const totalAgents = plan.phases.reduce((sum, p) => sum + p.agents.length, 0);

  return (
    <div
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-ldaja-blue/40 hover:shadow-md transition-all cursor-pointer group"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
    >
      {/* Header row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-ldaja-black truncate group-hover:text-ldaja-blue transition-colors">
            Spawn Plan {plan.id.substring(0, 8)}
          </h3>
          {plan.milestoneId && (
            <p className="text-xs text-gray-400 mt-0.5">
              Milestone: {plan.milestoneId}
            </p>
          )}
        </div>
        <Badge variant={status.variant} dot>
          {status.label}
        </Badge>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-4 text-xs mb-3">
        <div>
          <span className="text-gray-400">Phases: </span>
          <span className="font-semibold text-gray-700">{plan.phases.length}</span>
        </div>
        <div>
          <span className="text-gray-400">Agents: </span>
          <span className="font-semibold text-gray-700">{totalAgents}</span>
        </div>
        <div>
          <span className="text-gray-400">Tokens: </span>
          <span className="font-semibold text-gray-700">{fmtTokens(plan.totalEstimatedTokens)}</span>
        </div>
      </div>

      {/* Buffer indicator */}
      <div className="flex items-center gap-2 text-xs mb-3">
        <span className="text-gray-400">Buffer:</span>
        <span className="font-medium text-amber-600">{plan.bufferPercent}%</span>
        <span className="text-gray-300">|</span>
        <span className="text-gray-400">With Buffer:</span>
        <span className="font-medium text-gray-700">{fmtTokens(plan.totalWithBuffer)}</span>
      </div>

      {/* Date */}
      <div className="pt-3 border-t border-gray-100 text-xs text-gray-400">
        Created {fmtDate(plan.createdAt)}
        {plan.updatedAt && plan.updatedAt !== plan.createdAt && (
          <span> &middot; Updated {fmtDate(plan.updatedAt)}</span>
        )}
      </div>
    </div>
  );
}

// ── Plan Detail View ─────────────────────────────────────────────────

function PlanDetailView({
  plan,
  onBack,
  onAction,
}: {
  plan: SpawnPlan;
  onBack: () => void;
  onAction: () => void;
}) {
  const [isApproving, setIsApproving] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const status = planStatusConfig[plan.status] || planStatusConfig.draft;

  const handleApprove = async () => {
    setIsApproving(true);
    setActionError(null);
    try {
      await approveSpawnPlan(plan.id);
      onAction();
    } catch (err) {
      setActionError('Failed to approve plan. Please try again.');
    } finally {
      setIsApproving(false);
    }
  };

  const handleExecute = async () => {
    setIsExecuting(true);
    setActionError(null);
    try {
      await executeSpawnPlan(plan.id);
      onAction();
    } catch (err) {
      setActionError('Failed to execute plan. Please try again.');
    } finally {
      setIsExecuting(false);
    }
  };

  // Compute actual spent total
  const actualSpent = plan.phases.reduce(
    (sum, phase) =>
      sum + phase.agents.reduce((aSum, agent) => aSum + (agent.actualTokens || 0), 0),
    0,
  );

  // Completion stats
  const totalAgents = plan.phases.reduce((s, p) => s + p.agents.length, 0);
  const completedAgents = plan.phases.reduce(
    (s, p) => s + p.agents.filter((a) => a.status === 'completed').length,
    0,
  );
  const failedAgents = plan.phases.reduce(
    (s, p) => s + p.agents.filter((a) => a.status === 'failed').length,
    0,
  );

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-ldaja-blue transition-colors mb-4"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M15 19l-7-7 7-7" />
        </svg>
        Back to Spawn Plans
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-ldaja-black">Spawn Plan {plan.id.substring(0, 8)}</h1>
            <Badge variant={status.variant} dot>
              {status.label}
            </Badge>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {plan.milestoneId && <span>Milestone: {plan.milestoneId} &middot; </span>}
            Created {fmtDate(plan.createdAt)}
          </p>
        </div>
      </div>

      {/* Action Error */}
      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-700 text-xs">{actionError}</p>
          <button onClick={() => setActionError(null)} className="text-red-600 text-xs underline mt-1">
            Dismiss
          </button>
        </div>
      )}

      {/* Budget Summary */}
      <Card title="Budget Summary" subtitle="Token allocation for this spawn plan" className="mb-6">
        <BudgetSummaryGrid plan={plan} actualSpent={actualSpent} />
      </Card>

      {/* Phase Timeline */}
      <Card title="Phase Timeline" subtitle={`${plan.phases.length} phases, ${totalAgents} agents`} className="mb-6">
        <PhaseTimeline phases={plan.phases} />
      </Card>

      {/* Completion stats (for executing/completed) */}
      {(plan.status === 'executing' || plan.status === 'completed') && (
        <Card title="Execution Progress" className="mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Total Agents</p>
              <p className="text-lg font-bold text-gray-800 mt-1">{totalAgents}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Completed</p>
              <p className="text-lg font-bold text-green-600 mt-1">{completedAgents}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Failed</p>
              <p className="text-lg font-bold text-red-600 mt-1">{failedAgents}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Actual Spent</p>
              <p className="text-lg font-bold text-ldaja-blue mt-1">{fmtTokens(actualSpent)}</p>
            </div>
          </div>

          {/* Progress bar */}
          {totalAgents > 0 && (
            <div className="mt-4">
              <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden flex">
                {completedAgents > 0 && (
                  <div
                    className="h-full bg-green-500 transition-all"
                    style={{ width: `${(completedAgents / totalAgents) * 100}%` }}
                  />
                )}
                {failedAgents > 0 && (
                  <div
                    className="h-full bg-red-500 transition-all"
                    style={{ width: `${(failedAgents / totalAgents) * 100}%` }}
                  />
                )}
              </div>
              <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                <span>{completedAgents}/{totalAgents} completed</span>
                {failedAgents > 0 && <span className="text-red-500">{failedAgents} failed</span>}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-3 pt-2">
        {plan.status === 'pending_approval' && (
          <Button
            variant="primary"
            size="md"
            isLoading={isApproving}
            onClick={handleApprove}
            className="bg-green-600 hover:bg-green-700 focus:ring-green-500/50"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M5 13l4 4L19 7" />
            </svg>
            Approve Plan
          </Button>
        )}

        {plan.status === 'approved' && (
          <Button
            variant="primary"
            size="lg"
            isLoading={isExecuting}
            onClick={handleExecute}
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Execute Plan
          </Button>
        )}

        {plan.status === 'completed' && (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Plan execution completed
          </div>
        )}

        {plan.status === 'executing' && (
          <div className="flex items-center gap-2 text-sm text-purple-600">
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Plan is currently executing...
          </div>
        )}
      </div>
    </div>
  );
}

// ── Budget Summary Grid ──────────────────────────────────────────────

function BudgetSummaryGrid({ plan, actualSpent }: { plan: SpawnPlan; actualSpent: number }) {
  const totalEstimated = plan.totalEstimatedTokens;
  const bufferPct = plan.bufferPercent;
  const totalWithBuffer = plan.totalWithBuffer;
  const showActual = plan.status === 'executing' || plan.status === 'completed';
  const spentPct = totalWithBuffer > 0 ? (actualSpent / totalWithBuffer) * 100 : 0;

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Total Estimated</p>
          <p className="text-lg font-bold text-gray-800 mt-1">{fmtTokens(totalEstimated)}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Buffer ({bufferPct}%)</p>
          <p className="text-lg font-bold text-amber-600 mt-1">{fmtTokens(totalWithBuffer - totalEstimated)}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Total with Buffer</p>
          <p className="text-lg font-bold text-ldaja-blue mt-1">{fmtTokens(totalWithBuffer)}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Actual Spent</p>
          <p className={`text-lg font-bold mt-1 ${showActual ? 'text-green-600' : 'text-gray-300'}`}>
            {showActual ? fmtTokens(actualSpent) : '---'}
          </p>
        </div>
      </div>

      {/* Budget progress bar (only during execution) */}
      {showActual && totalWithBuffer > 0 && (
        <div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                spentPct > 100 ? 'bg-red-500' : spentPct > 80 ? 'bg-amber-500' : 'bg-ldaja-blue'
              }`}
              style={{ width: `${Math.min(spentPct, 100)}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-gray-400 mt-1">
            <span>{spentPct.toFixed(1)}% of budget used</span>
            <span>{fmtTokens(Math.max(totalWithBuffer - actualSpent, 0))} remaining</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Phase Timeline ───────────────────────────────────────────────────

function PhaseTimeline({ phases }: { phases: SpawnPlanPhase[] }) {
  if (phases.length === 0) {
    return (
      <p className="text-sm text-gray-400 py-6 text-center">No phases defined in this plan.</p>
    );
  }

  return (
    <div className="relative">
      {phases.map((phase, idx) => {
        const phaseStatus = phaseStatusConfig[phase.status] || phaseStatusConfig.pending;
        const isLast = idx === phases.length - 1;

        return (
          <div key={phase.phaseNumber} className="relative flex gap-4">
            {/* Timeline connector */}
            <div className="flex flex-col items-center shrink-0">
              {/* Dot */}
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                  phase.status === 'completed'
                    ? 'bg-green-500'
                    : phase.status === 'in_progress'
                    ? 'bg-purple-500'
                    : phase.status === 'failed'
                    ? 'bg-red-500'
                    : 'bg-gray-300'
                }`}
              >
                {phase.phaseNumber}
              </div>
              {/* Line */}
              {!isLast && (
                <div className="w-0.5 flex-1 bg-gray-200 my-1" />
              )}
            </div>

            {/* Phase content */}
            <div className={`flex-1 pb-6 ${isLast ? '' : ''}`}>
              {/* Phase header */}
              <div className="flex items-center gap-2 mb-2">
                <h4 className="text-sm font-semibold text-gray-800">
                  Phase {phase.phaseNumber}
                </h4>
                <Badge variant={phaseStatus.variant} dot>
                  {phaseStatus.label}
                </Badge>
                {phase.parallelizable && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-ldaja-blue rounded font-medium">
                    Parallelizable
                  </span>
                )}
                <span className="text-xs text-gray-400 ml-auto">
                  {fmtTokens(phase.estimatedTokens)} tokens est.
                </span>
              </div>

              {/* Agents list */}
              <div className="space-y-2">
                {phase.agents.map((agent) => (
                  <AgentRow key={agent.agentId} agent={agent} />
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Agent Row ────────────────────────────────────────────────────────

function AgentRow({ agent }: { agent: SpawnPlanAgent }) {
  const status = agentStatusConfig[agent.status] || agentStatusConfig.pending;
  const avatarColor = agentAvatarColor(agent.agentId);

  return (
    <div className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg">
      {/* Agent avatar */}
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0 ${avatarColor}`}
      >
        {agent.agentId.substring(0, 2).toUpperCase()}
      </div>

      {/* Agent info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-800 capitalize">{agent.agentId}</span>
        </div>
        <p className="text-xs text-gray-500 truncate">{agent.task}</p>
      </div>

      {/* Token estimate */}
      <div className="text-right shrink-0">
        <p className="text-xs font-medium text-gray-700 tabular-nums">{fmtTokens(agent.estimatedTokens)}</p>
        {agent.actualTokens !== undefined && agent.actualTokens > 0 && (
          <p className="text-[10px] text-green-600 tabular-nums">
            actual: {fmtTokens(agent.actualTokens)}
          </p>
        )}
      </div>

      {/* Status badge */}
      <Badge variant={status.variant} dot>
        {status.label}
      </Badge>
    </div>
  );
}
